/**
 * Human ticket references, browser side.
 *
 * The admin app writes ticket rows directly (see #87), so it allocates the reference itself
 * rather than asking a function for one. `tickets.reference` carries a unique index, which
 * turns a collision into a 409 the caller retries — the same trade
 * `functions/internal-intake/reference.js` makes on the email path, for the same reason: an
 * Appwrite sequence would need a read-modify-write that two concurrent requests race.
 *
 * Deliberately a second copy rather than a shared module. `sharedModules` in
 * appwrite.config.json copies files into *function* directories at deploy time, so a site
 * cannot borrow through it, and `apps/shared` is outside this app's `src/` — CRA's
 * ModuleScopePlugin rejects an import that reaches out of it, and nothing here disables that
 * plugin. The two copies must agree on the format, which is what the format test beside each
 * of them exists to hold.
 */

// Crockford base32: no I, L, O or U, so a reference read down the phone is unambiguous and
// cannot accidentally spell anything. Must match functions/internal-intake/reference.js.
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const LENGTH = 6;

export const REFERENCE_PATTERN = new RegExp(`^PG-[${ALPHABET}]{${LENGTH}}$`);

/** ~1.07 billion values. A collision is a retry, not a bug. */
export function generateReference() {
  let out = '';
  for (let i = 0; i < LENGTH; i += 1) {
    out += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  }
  return `PG-${out}`;
}

/**
 * Run `attempt` with fresh references until Appwrite stops answering 409.
 *
 * A 409 here means the unique index rejected the reference, so the only sensible response is
 * a different one. Any other error is the caller's problem and is rethrown untouched.
 *
 * @param {(reference: string) => Promise<any>} attempt
 * @param {number} maxAttempts
 */
export async function withReference(attempt, maxAttempts = 5) {
  let tries = 0;
  for (;;) {
    try {
      return await attempt(generateReference());
    } catch (error) {
      tries += 1;
      if (error && error.code === 409 && tries < maxAttempts) continue;
      throw error;
    }
  }
}
